import { useEffect, useState } from "react";
import { Printer, Truck, Car, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import api, { errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GradButton } from "@/components/erp";
import { SablonluBelge, SablonSec, useSablon } from "@/components/sablon/SablonluBelge";
import { irsaliyeVeri } from "@/lib/belgeVeri";

export const Tasima = ({ v }) => (v.teslim_tipi === "kargo" ? (
  <span className="inline-flex items-center gap-1.5 text-xs"><Truck className="w-3.5 h-3.5 text-muted-foreground" />{v.kargo_ad}
    {v.takip_no ? (v.takip_url ? <a href={v.takip_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`takip-link-${v.no}`} className="font-mono text-sky-300 hover:underline inline-flex items-center gap-0.5">{v.takip_no}<ExternalLink className="w-3 h-3" /></a> : <span className="font-mono">{v.takip_no}</span>) : <span className="text-amber-300/80">takip no yok</span>}</span>
) : <span className="inline-flex items-center gap-1.5 text-xs"><Car className="w-3.5 h-3.5 text-muted-foreground" /><span className="font-mono">{v.plaka}</span>{v.sofor ? ` · ${v.sofor}` : ""}</span>);

const Satir = ({ l, v }) => (v ? <div><span className="text-neutral-500">{l}: </span>{v}</div> : null);

const Gorsel = ({ id, tur, className, testid }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/sevkiyatlar/${id}/dosya/${tur}`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id, tur]);
  return src ? <img src={src} alt={tur} className={className} data-testid={testid} /> : null;
};

const TeslimAlan = ({ v }) => (
  <div className="border-t border-neutral-300 pt-2" data-testid="irsaliye-teslim-alan">
    {v.imza_yolu && <Gorsel id={v.id} tur="imza" className="h-20 mx-auto -mt-24 mb-1 object-contain" testid="irsaliye-imza" />}
    Teslim alan{v.teslim_alan ? ` · ${v.teslim_alan}` : ""}
    {v.teslim_tarihi && <div className="text-[11px]">{new Date(v.teslim_tarihi).toLocaleString("tr-TR")}</div>}
  </div>
);

const Belge = ({ v }) => (
  <div className="print-area bg-white text-neutral-900 rounded-xl p-8 text-[13px] leading-relaxed" data-testid="irsaliye-belge">
    <div className="flex justify-between gap-6 border-b border-neutral-300 pb-4">
      <div>
        {v.firma?.logo_url && <img src={v.firma.logo_url} alt="" className="h-10 mb-2" />}
        <div className="font-bold text-base">{v.firma?.unvan}</div>
        <div className="text-neutral-600 max-w-xs">{v.firma?.adres}</div>
        <Satir l="Tel" v={v.firma?.telefon} /><Satir l="V.D. / No" v={[v.firma?.vergi_dairesi, v.firma?.vergi_no].filter(Boolean).join(" / ")} />
      </div>
      <div className="text-right">
        <div className="text-xl font-bold tracking-wide">SEVK İRSALİYESİ</div>
        <div className="font-mono mt-1" data-testid="irsaliye-no">{v.no}</div>
        <div>Tarih: {v.tarih}</div><div>Sipariş: {v.siparis_no}</div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-6 py-4 border-b border-neutral-300">
      <div><div className="text-[11px] uppercase text-neutral-500 mb-1">Alıcı</div><div className="font-semibold">{v.cari_unvan}</div><div className="text-neutral-600">{v.teslim_adresi || "Adres belirtilmedi"}</div>
        <Satir l="V.D. / No" v={[v.cari_vergi_dairesi, v.cari_vergi_no].filter(Boolean).join(" / ")} /></div>
      <div><div className="text-[11px] uppercase text-neutral-500 mb-1">Taşıma</div>
        {v.teslim_tipi === "kargo" ? <><div>{v.kargo_ad}</div><Satir l="Takip no" v={v.takip_no} /></> : <><div>Kendi aracımız</div><Satir l="Plaka" v={v.plaka} /><Satir l="Şoför" v={v.sofor} /></>}
        <Satir l="Koli / palet" v={v.koli_adedi} /></div>
    </div>
    <table className="w-full mt-4">
      <thead><tr className="text-left text-[11px] uppercase text-neutral-500 border-b border-neutral-300"><th className="py-2">Kod</th><th>Ürün</th><th>Lot</th><th className="text-right">Miktar</th><th className="text-right">Kalan</th></tr></thead>
      <tbody>{v.kalemler.map((k) => (
        <tr key={k.kalem_idx} className="border-b border-neutral-200"><td className="py-2 font-mono text-xs">{k.kod}</td><td>{k.ad}</td>
          <td className="text-xs">{k.lotlar.map((l) => l.lot).filter(Boolean).join(", ")}</td>
          <td className="text-right font-semibold">{k.miktar} {k.birim}</td><td className="text-right text-neutral-500">{k.kalan_miktar} {k.birim}</td></tr>
      ))}</tbody>
    </table>
    {v.koliler?.length > 0 && (
      <div className="mt-6" data-testid="irsaliye-koli-listesi">
        <div className="text-[11px] uppercase text-neutral-500 mb-2">Koli / paket listesi ({v.koliler.length} koli)</div>
        <table className="w-full text-sm"><tbody>{v.koliler.map((k) => k.kalemler.map((x, j) => (
          <tr key={`${k.no}-${j}`} className="border-b border-neutral-200">
            <td className="py-1.5 w-28 font-semibold">{j === 0 ? `Koli ${k.no}` : ""}</td><td className="text-xs text-neutral-500 w-28">{j === 0 ? k.tip : ""}</td>
            <td className="font-mono text-xs">{x.kod}</td><td>{x.ad}</td><td className="text-right">{x.miktar} {x.birim}</td>
          </tr>
        )))}</tbody></table>
      </div>
    )}
    {v.aciklama && <div className="mt-4 text-neutral-600">Not: {v.aciklama}</div>}
    <div className="grid grid-cols-2 gap-6 mt-28 text-center text-neutral-500"><div className="border-t border-neutral-300 pt-2">Teslim eden · {v.sevk_eden_ad}</div><TeslimAlan v={v} /></div>
    {v.foto_yolu && <div className="mt-6 print:hidden"><div className="text-[11px] uppercase text-neutral-500 mb-1">Teslim fotoğrafı</div><Gorsel id={v.id} tur="foto" className="max-h-72 rounded-lg" testid="irsaliye-foto" /></div>}
  </div>
);

const EPOSTA_ETIKET = { gonderildi: "E-postayla gönderildi", gonderiliyor: "E-posta gönderiliyor", basarisiz: "E-posta gönderilemedi", adres_yok: "Carinin e-posta adresi yok" };

const EpostaSerit = ({ v, onYenile }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (v.durum !== "teslim_edildi") return null;
  const e = v.eposta;
  const gonder = async () => {
    setBusy(true);
    try { onYenile((await api.post(`/sevkiyatlar/${v.id}/eposta`)).data); toast.success(`İmzalı irsaliye ${v.cari_email} adresine gönderildi`); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 mb-3 text-xs rounded-xl bg-white/[0.04] p-3" data-testid="irsaliye-eposta">
      <span className={`inline-flex items-center gap-1.5 ${e?.durum === "gonderildi" ? "text-emerald-300" : e?.durum === "basarisiz" ? "text-red-300" : "text-muted-foreground"}`} data-testid="irsaliye-eposta-durum">
        <Mail className="w-3.5 h-3.5" />{e ? `${EPOSTA_ETIKET[e.durum] || e.durum}${e.adres ? ` · ${e.adres}` : ""}${e.tarih ? ` · ${new Date(e.tarih).toLocaleString("tr-TR")}` : ""}${e.mesaj ? ` · ${e.mesaj}` : ""}` : "E-posta gönderilmedi"}</span>
      {can(user, "siparis:sevk") && v.cari_email && <button onClick={gonder} disabled={busy} data-testid="irsaliye-eposta-gonder" className="shrink-0 px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/10 disabled:opacity-50">{e?.durum === "gonderildi" ? "Tekrar gönder" : "E-postayla gönder"}</button>}
    </div>
  );
};

export const IrsaliyeDialog = ({ id, yazdir, onClose }) => {
  const [v, setV] = useState(null);
  const [sid, setSid] = useState(null);
  const s = useSablon("irsaliye", sid);
  useEffect(() => {
    if (v?.eposta?.durum !== "gonderiliyor") return undefined;
    const t = setInterval(() => api.get(`/sevkiyatlar/${id}`).then((r) => setV(r.data)), 3000);
    return () => clearInterval(t);
  }, [id, v?.eposta?.durum]);
  useEffect(() => {
    api.get(`/sevkiyatlar/${id}`).then((r) => setV(r.data));
  }, [id]);
  useEffect(() => {
    if (v && s && yazdir) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [v, s, yazdir]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass max-w-3xl max-h-[92vh] overflow-y-auto erp-scroll" data-testid="irsaliye-dialog">
        <DialogHeader className="no-print"><DialogTitle className="font-head flex flex-wrap items-center justify-between gap-3 pr-6">İrsaliye
          <div className="flex items-center gap-2"><SablonSec s={s} deger={sid} onChange={setSid} /><GradButton data-testid="irsaliye-yazdir" onClick={() => window.print()} className="!py-1.5"><Printer className="w-4 h-4" />Yazdır</GradButton></div></DialogTitle></DialogHeader>
        {v && s ? <><EpostaSerit v={v} onYenile={(d) => setV((x) => ({ ...x, ...d }))} />
          <SablonluBelge d={irsaliyeVeri(v)} sablon={s.sablon} firma={s.firma}>
            {v.koliler?.length > 0 && <div className="mt-6" data-testid="irsaliye-koli-listesi"><div className="text-[11px] uppercase text-neutral-500 mb-2">Koli / paket listesi ({v.koliler.length} koli)</div>
              <table className="w-full text-sm"><tbody>{v.koliler.map((k) => k.kalemler.map((x, j) => (
                <tr key={`${k.no}-${j}`} className="border-b border-neutral-200"><td className="py-1.5 w-28 font-semibold">{j === 0 ? `Koli ${k.no}` : ""}</td><td className="text-xs text-neutral-500 w-28">{j === 0 ? k.tip : ""}</td>
                  <td className="font-mono text-xs">{x.kod}</td><td>{x.ad}</td><td className="text-right">{x.miktar} {x.birim}</td></tr>)))}</tbody></table></div>}
          </SablonluBelge></> : <div className="py-10 text-center text-sm text-muted-foreground">Yükleniyor…</div>}
      </DialogContent>
    </Dialog>
  );
};
